const CAP17 = {
  id: 17,
  slug: "cap17-hashing-firmas-digitales-i",
  title: "Capítulo 17 — El hashing y las firmas digitales (I)",
  sections: [
    {
      heading: "¿Qué es el hashing?",
      text: [
        "El hashing es un proceso **fundamental en la criptografía y en la tecnología blockchain**. Consiste en transformar una entrada de datos de **longitud variable** (como un documento, una imagen o una transacción) en un valor de **longitud fija**, conocido como **hash**.",
        "Este hash se genera mediante una **función hash criptográfica**, que sirve para **proteger, verificar y asegurar la integridad de los datos**.",
        "Un hash funciona como una **huella digital única**: representa un archivo, texto o mensaje de manera compacta, segura e irreversible."
      ]
    },

    {
      heading: "¿Para qué se usa el hashing en blockchain?",
      list: [
        "🔹 Para asegurar que **los bloques y transacciones no hayan sido alterados**.",
        "🔹 Para crear el encadenamiento de bloques mediante el **hash del bloque anterior**.",
        "🔹 Para validar la integridad de documentos, contratos inteligentes y registros.",
        "🔹 Para generar direcciones públicas y protección de claves privadas.",
        "🔹 Es una parte clave del funcionamiento de las **firmas digitales** y de los **árboles de Merkle**."
      ]
    },

    {
      heading: "¿Cómo funciona el hashing?",
      text: [
        "El hashing convierte cualquier entrada (texto, documento, número, etc.) en una salida fija. Por ejemplo:",
        "Usando **SHA-256**, uno de los algoritmos más populares, siempre se obtiene un hash de **256 bits** (64 caracteres en hexadecimal), sin importar el tamaño de los datos originales.",
        "Eso significa que puedes introducir una palabra, un libro completo o una imagen, y siempre obtendrás una salida de tamaño **exactamente igual**."
      ],
      pre: `Entrada: "Blockchain"
Salida (SHA-256):
6251e9e790ca4a6c0bba57a09a5e7fcd49adee29d14f93454b768400ce499e33`
    },

    {
      heading: "Características clave del hashing",
      list: [
        "**Determinismo**: la misma entrada siempre produce el mismo hash.",
        "**Sensibilidad extrema** (Efecto avalancha): un cambio mínimo en la entrada genera un hash completamente diferente.",
        "**Unidireccional**: no se puede recuperar la información original a partir del hash (irreversible).",
        "**Resistente a colisiones**: es extremadamente difícil encontrar dos entradas distintas con el mismo hash.",
        "**Eficiente**: genera resultados de forma rápida, incluso con grandes volúmenes de datos."
      ]
    },

    {
      heading: "Ejemplo del efecto avalancha",
      text: [
        "Observa cómo cambiar apenas un carácter produce un hash completamente diferente:"
      ],
      pre: `Entrada 1: "Hola"
Hash:
f688ae26e13a7136e58c6610ac3e2b18cf7c3675e1e7d85652c5f5a4b4f3e7f2

Entrada 2: "hola"
Hash:
53048e2681941ef99b2e29b76d6b44d979f90c0079b0b78f776b9e3f6b34f94d`
    },

    {
      heading: "¿Por qué el hashing es tan importante en blockchain?",
      text: [
        "El hashing permite verificar si un dato fue **modificado**, **alterado**, **duplicado** o **manipulado**.",
        "En blockchain, cada bloque contiene el hash del bloque anterior. Si alguien intenta modificar cualquier parte, los hashes cambiarán y se detectará el intento de manipulación.",
        "Esto mantiene la blockchain **inmutable, segura y resistente a ataques**."
      ]
    },

    {
      heading: "Hashing y seguridad digital",
      text: [
        "El hashing también se utiliza fuera de blockchain, por ejemplo:",
      ],
      list: [
        "🔹 Para verificar archivos descargados mediante hashes oficiales (SHA-256).",
        "🔹 Para proteger contraseñas en bases de datos (no se guardan las contraseñas, sino sus hashes).",
        "🔹 Para detectar cambios no autorizados en sistemas (integridad de archivos).",
        "🔹 En certificados digitales, autenticación y aplicaciones criptográficas."
      ]
    },

    {
      heading: "Conexión con firmas digitales",
      text: [
        "Las **firmas digitales** utilizan hashing como parte esencial del proceso.",
        "En una firma digital, primero se aplica hashing al documento o transacción, y luego ese hash se firma criptográficamente usando la **clave privada del usuario**.",
        "Esto permite verificar:",
        "🔹 Que el contenido no ha sido modificado (integridad).",
        "🔹 Que fue firmado por la persona correcta (autenticidad).",
        "🔹 Que el firmante no puede negar su participación (no repudio)."
      ]
    },

    {
      heading: "Resumen rápido",
      list: [
        "🔹 Hashing = Huella digital irreversible de un dato.",
        "🔹 Protege bloques, transacciones, identidades y registros.",
        "🔹 Permite detectar cambios, fraudes y manipulaciones.",
        "🔹 Es la base de las **firmas digitales**, los **árboles de Merkle** y la **inmutabilidad** en blockchain.",
        "🔹 Sin hashing, blockchain no existiría tal como la conocemos."
      ]
    },

    {
      heading: "Lo que viene",
      text: [
        "En el próximo capítulo (Parte II) aprenderás:",
        "🔹 Cómo se crea una firma digital.",
        "🔹 Cómo funcionan las claves públicas y privadas.",
        "🔹 Ejemplos visuales y en pseudocódigo.",
        "🔹 Cómo se firma una transacción en Bitcoin y Ethereum."
      ]
    }
  ]
};

export default CAP17;
